/**
 * DL Fork: BI bridge — React wiring.
 *
 * Activation gate + inbound message handling (navigate / data-changed relay) +
 * outbound context emitter. The pure protocol helpers live in `biBridge.ts`.
 *
 * SECURITY (PRD §4):
 *  - The channel activates ONLY when: this is the embedded build AND we are in
 *    an iframe AND a `targetOrigin` was captured at bundle load AND that origin
 *    validates against the health endpoint's `embedding.postMessage` allowlist.
 *    Fail-closed: until health loads (or if it lacks the field), the bridge is
 *    inert. The bare URL param is NEVER trusted on its own.
 *  - Inbound `message` events are accepted ONLY when `event.origin` equals the
 *    validated origin. Outbound posts go ONLY to that validated origin.
 */
import { isAllowedPostMessageOrigin } from '@lightdash/common';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useActiveProjectUuid } from '../../../hooks/useActiveProject';
import useHealth from '../../../hooks/health/useHealth';
import {
    composeNavPath,
    deriveBiContext,
    DL_INTERNAL_DATA_CHANGED_EVENT,
    type DlContextMessage,
    type DlNavigateMessage,
    parseNavigateRequest,
} from './biBridge';
import { getCapturedTargetOrigin, IS_EMBEDDED_BUILD } from './parentMessaging';

const CONTEXT_EMIT_THROTTLE_MS = 500;

/**
 * Resolve the validated wrapper origin, or `null` when the bridge must stay
 * inert. Cheap enough to call unconditionally (health is fetched app-wide; the
 * captured origin is a module constant).
 */
export function useValidatedBridgeOrigin(): string | null {
    const { data: health } = useHealth();
    const captured = getCapturedTargetOrigin();

    return useMemo(() => {
        if (!IS_EMBEDDED_BUILD) return null;
        if (typeof window === 'undefined' || window.parent === window) {
            return null;
        }
        if (!captured) return null;
        const allowlist = health?.embedding?.postMessage;
        // Fail-closed: no allowlist yet (health still loading) ⇒ inert.
        if (!allowlist) return null;
        return isAllowedPostMessageOrigin(captured, allowlist)
            ? captured
            : null;
    }, [captured, health?.embedding?.postMessage]);
}

/**
 * Inbound bridge: listen for `dl:navigate` (validated + fork-composed path,
 * queued while the active project is still resolving) and `dl:data-changed`
 * (relayed via an internal window event). Only mount this when `origin` is a
 * validated non-null origin.
 */
export function useBiInboundBridge(origin: string): void {
    const navigate = useNavigate();
    const { activeProjectUuid, isLoading } = useActiveProjectUuid();

    // Latest project-resolution state, read from the stable message handler.
    const projectStateRef = useRef({ activeProjectUuid, isLoading });
    projectStateRef.current = { activeProjectUuid, isLoading };

    // A single pending navigation held while the project is still loading
    // (latest wins — chained deep-links collapse to the final destination).
    const pendingNavRef = useRef<DlNavigateMessage | null>(null);

    const runNavigate = useCallback(
        (request: DlNavigateMessage) => {
            const { activeProjectUuid: projectUuid, isLoading: loading } =
                projectStateRef.current;
            if (loading || !projectUuid) {
                pendingNavRef.current = request;
                return;
            }
            const path = composeNavPath(projectUuid, request);
            if (path) void navigate(path);
        },
        [navigate],
    );

    // Message listener — accepts ONLY the validated origin.
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.origin !== origin) return;
            const data = event.data as unknown;
            if (typeof data !== 'object' || data === null) return;
            const type = (data as { type?: unknown }).type;
            if (type === 'dl:navigate') {
                const request = parseNavigateRequest(data);
                if (request) runNavigate(request);
            } else if (type === 'dl:data-changed') {
                window.dispatchEvent(
                    new CustomEvent(DL_INTERNAL_DATA_CHANGED_EVENT),
                );
            }
            // Unknown types ignored (forward-compatibility).
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [origin, runNavigate]);

    // Flush a queued navigation once the project has resolved.
    useEffect(() => {
        if (isLoading || !activeProjectUuid || !pendingNavRef.current) return;
        const request = pendingNavRef.current;
        pendingNavRef.current = null;
        const path = composeNavPath(activeProjectUuid, request);
        if (path) void navigate(path);
    }, [activeProjectUuid, isLoading, navigate]);
}

/**
 * Global refresh handler: on the internal data-changed event, invalidate all
 * queries. Covers EVERY page (non-dashboard pages get invalidation only; the
 * open-dashboard re-seed is handled by the provider-scoped listener). The
 * internal event only ever fires from the active inbound bridge, so this is
 * inert unless the channel is active.
 */
export function useBiGlobalInvalidation(): void {
    const queryClient = useQueryClient();
    useEffect(() => {
        const handler = () => {
            void queryClient.invalidateQueries();
        };
        window.addEventListener(DL_INTERNAL_DATA_CHANGED_EVENT, handler);
        return () =>
            window.removeEventListener(DL_INTERNAL_DATA_CHANGED_EVENT, handler);
    }, [queryClient]);
}

/**
 * Outbound context emitter: on ROUTE change (pathname only — search-param
 * changes never re-emit), throttled to ≥500ms, post a `dl:context` describing
 * the BI screen the operator is on. Silent on non-BI-content screens. `name` is
 * best-effort from the query cache (never blocks).
 */
export function useBiContextEmitter(origin: string): void {
    // Depend on pathname ONLY — search-param changes must not re-emit.
    const { pathname } = useLocation();
    const queryClient = useQueryClient();

    const lastEmitTsRef = useRef(0);
    const lastPathnameRef = useRef<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (pathname === lastPathnameRef.current) return;
        lastPathnameRef.current = pathname;

        const context = deriveBiContext(pathname);
        if (!context) return; // off BI content — stay silent

        const emit = () => {
            lastEmitTsRef.current = Date.now();

            let name: string | undefined;
            try {
                if (context.dashboardUuid) {
                    name = (
                        queryClient.getQueryData([
                            'saved_dashboard_query',
                            context.dashboardUuid,
                        ]) as { name?: string } | undefined
                    )?.name;
                } else if (context.chartUuid) {
                    name = (
                        queryClient.getQueryData([
                            'saved_query',
                            context.chartUuid,
                        ]) as { name?: string } | undefined
                    )?.name;
                } else if (context.spaceUuid) {
                    name = (
                        queryClient.getQueryData([
                            'space',
                            context.projectUuid,
                            context.spaceUuid,
                        ]) as { name?: string } | undefined
                    )?.name;
                }
            } catch {
                name = undefined;
            }

            const message: DlContextMessage = {
                type: 'dl:context',
                projectUuid: context.projectUuid,
                dashboardUuid: context.dashboardUuid,
                chartUuid: context.chartUuid,
                spaceUuid: context.spaceUuid,
                mode: context.mode,
                name,
            };

            try {
                window.parent.postMessage(message, origin);
            } catch {
                // wrapper torn down / cross-origin anomaly — non-actionable.
            }
        };

        const elapsed = Date.now() - lastEmitTsRef.current;
        if (elapsed >= CONTEXT_EMIT_THROTTLE_MS) {
            emit();
        } else {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(
                emit,
                CONTEXT_EMIT_THROTTLE_MS - elapsed,
            );
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [origin, pathname, queryClient]);
}
